import { useEffect, useMemo, useState } from 'react'
import {
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiCompass,
  FiZap,
  FiBookOpen,
} from 'react-icons/fi'
import type { PageGuide, GuideStep } from './guideTypes'
import { pickLang } from './guideTypes'
import GuideScreenshot from './GuideScreenshot'

interface Props {
  guide: PageGuide
  language: string
  open: boolean
  onClose: () => void
}

function flattenSteps(guide: PageGuide): GuideStep[] {
  return guide.sections.flatMap((s) => s.steps)
}

export default function SmartGuidePanel({ guide, language, open, onClose }: Props) {
  const isAr = language === 'ar'
  const steps = useMemo(() => flattenSteps(guide), [guide])
  const [index, setIndex] = useState(0)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (open) {
      setIndex(0)
      const t = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(t)
    }
    setEntered(false)
  }, [open, guide])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') setIndex((i) => (isAr ? Math.min(steps.length - 1, i + 1) : Math.max(0, i - 1)))
      if (e.key === 'ArrowRight') setIndex((i) => (isAr ? Math.max(0, i - 1) : Math.min(steps.length - 1, i + 1)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, steps.length, isAr])

  if (!open) return null

  const step = steps[index]
  const accent = guide.accent ?? '#14b8a6'
  const title = pickLang(guide.title, language)
  const summary = pickLang(guide.summary, language)

  const go = (dir: -1 | 1) => {
    setIndex((i) => Math.min(steps.length - 1, Math.max(0, i + dir)))
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 border-0 bg-slate-950/70 backdrop-blur-md transition-opacity"
        style={{ opacity: entered ? 1 : 0 }}
        onClick={onClose}
        aria-label={isAr ? 'إغلاق' : 'Close'}
      />

      {/* Orb decorations */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="guide-orb absolute -top-24 start-1/4 h-72 w-72 rounded-full opacity-30 blur-3xl"
          style={{ background: accent }}
        />
        <div
          className="guide-orb-slow absolute -bottom-32 end-1/4 h-80 w-80 rounded-full opacity-20 blur-3xl"
          style={{ background: '#38bdf8' }}
        />
      </div>

      <div
        className="guide-panel relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] shadow-2xl transition-all duration-500"
        dir={isAr ? 'rtl' : 'ltr'}
        style={{
          opacity: entered ? 1 : 0,
          transform: entered ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.96)',
          background:
            'linear-gradient(160deg, rgba(15,23,42,0.97) 0%, rgba(2,44,48,0.96) 45%, rgba(15,23,42,0.98) 100%)',
          border: '1px solid rgba(45,212,191,0.25)',
          boxShadow: `0 0 0 1px rgba(255,255,255,0.04), 0 40px 80px -20px rgba(0,0,0,0.65), 0 0 60px -10px ${accent}55`,
        }}
      >
        {/* Header */}
        <header className="relative flex shrink-0 items-start justify-between gap-4 border-b border-teal-400/15 px-5 py-4 sm:px-7 sm:py-5">
          <div className="flex min-w-0 items-start gap-3">
            <div
              className="guide-icon-glow flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-2xl"
              style={{
                background: `linear-gradient(135deg, ${accent}, #0f172a)`,
                boxShadow: `0 0 24px ${accent}66`,
              }}
            >
              {guide.emoji ?? '✨'}
            </div>
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-teal-300/30 bg-teal-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-teal-200">
                  <FiCompass className="h-3 w-3" />
                  {isAr ? 'مرشد الشاشة' : language === 'fr' ? 'Guide écran' : 'Screen guide'}
                </span>
                <span className="text-[10px] text-slate-400">
                  {index + 1} / {steps.length}
                </span>
              </div>
              <h2 className="truncate text-xl font-black tracking-tight text-white sm:text-2xl">{title}</h2>
              <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-teal-100/70">{summary}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 bg-white/5 p-2.5 text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <FiX className="h-5 w-5" />
          </button>
        </header>

        {/* Body */}
        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">
          {guide.gallery && guide.gallery.length > 0 && index === 0 && (
            <div className="mb-5 grid gap-3 sm:grid-cols-2">
              {guide.gallery.map((g, i) => (
                <GuideScreenshot key={i} shot={g} language={language} />
              ))}
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-teal-200/80">
                <FiBookOpen className="h-3.5 w-3.5" />
                {isAr ? 'الخطوة الحالية' : language === 'fr' ? 'Étape actuelle' : 'Current step'}
              </div>
              <h3
                className="text-2xl font-black leading-snug text-white"
                style={{ textShadow: `0 0 40px ${accent}44` }}
              >
                {step ? pickLang(step.title, language) : ''}
              </h3>
              <p className="text-[15px] leading-8 text-slate-300">
                {step ? pickLang(step.body, language) : ''}
              </p>

              {/* Progress dots */}
              <div className="flex flex-wrap gap-2 pt-2">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIndex(i)}
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: i === index ? 28 : 8,
                      background: i === index ? accent : 'rgba(148,163,184,0.35)',
                    }}
                    aria-label={`Step ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="guide-shot-enter" key={index}>
              {step?.shot ? (
                <GuideScreenshot shot={step.shot} language={language} />
              ) : (
                <GuideScreenshot
                  shot={{
                    motif: 'dashboard',
                    caption: {
                      ar: 'نظرة على الشاشة',
                      en: 'Screen overview',
                      fr: 'Aperçu',
                    },
                  }}
                  language={language}
                />
              )}
            </div>
          </div>

          {guide.tips && guide.tips.length > 0 && index === steps.length - 1 && (
            <div className="mt-6 rounded-2xl border border-amber-300/25 bg-amber-400/10 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-200">
                <FiZap className="h-4 w-4" />
                {isAr ? 'نصائح ذهبية' : language === 'fr' ? 'Astuces' : 'Pro tips'}
              </div>
              <ul className="space-y-2 text-sm leading-relaxed text-amber-50/90">
                {guide.tips.map((tip, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                    <span>{pickLang(tip, language)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-teal-400/15 bg-black/20 px-5 py-3.5 sm:px-7">
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={index === 0}
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 transition enabled:hover:bg-white/10 disabled:opacity-30"
          >
            {isAr ? <FiChevronRight className="h-4 w-4" /> : <FiChevronLeft className="h-4 w-4" />}
            {isAr ? 'السابق' : language === 'fr' ? 'Précédent' : 'Back'}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="hidden rounded-xl px-4 py-2 text-sm text-slate-400 transition hover:text-white sm:inline-flex"
          >
            {isAr ? 'تخطي' : language === 'fr' ? 'Passer' : 'Skip'}
          </button>

          {index < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => go(1)}
              className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-slate-950 shadow-lg transition hover:brightness-110"
              style={{ background: `linear-gradient(135deg, ${accent}, #5eead4)` }}
            >
              {isAr ? 'التالي' : language === 'fr' ? 'Suivant' : 'Next'}
              {isAr ? <FiChevronLeft className="h-4 w-4" /> : <FiChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-slate-950 shadow-lg transition hover:brightness-110"
              style={{ background: `linear-gradient(135deg, ${accent}, #5eead4)` }}
            >
              {isAr ? 'فهمت، هيا نبدأ' : language === 'fr' ? 'Compris, c’est parti' : 'Got it, let’s go'}
            </button>
          )}
        </footer>
      </div>
    </div>
  )
}
